import React from "react";
import { FilterBar } from "./FilterBar";
import { FilterPopup } from "./FilterPopup";
import { useStateValue } from "../App";
import queryString from "qs";

const textMatch = (needle, haystack) =>
  haystack
    .toLowerCase()
    .replace(" ", "")
    .indexOf(needle.toLowerCase().replace(" ", "")) !== -1;

export const Filter = React.memo(
  ({ filterList, filterGroups, isActive, setIsActive }) => {
    // Large filter modal or filter popup on mobile
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [searchValue, updateSearchValue] = React.useState("");

    const [
      { activeFilterGroups, activeFilters, uid, activeSubtractions },
      dispatch
    ] = useStateValue();

    const removeActiveFilters = () => {
      dispatch({ type: "removeActiveFiters" });
    };

    const handleOnFilterClick = (filter, e) => {
      if (e.target.checked) {
        dispatch({ type: "addFilterToActiveFilters", payload: filter });
        dispatch({ type: "removeActiveSubtractions", payload: filter });
      } else {
        if (activeFilters.indexOf(filter) !== -1) {
          dispatch({ type: "removeFilterFromActiveFilters", payload: filter });
          dispatch({ type: "addActiveSubtractions", payload: filter });
        } else {
          dispatch({ type: "addActiveSubtractions", payload: filter });
        }
      }

      window.dataLayer.push({
        event: `switchFilter${e.target.checked ? "On" : "Off"}`,
        filter: filterList[filter].title
      });
    };

    const handleOnGroupFilterClick = (group, e) => {
      setIsExpanded(true);
      if (e.target.checked) {
        dispatch({ type: "addFilterGroupToActiveFilters", payload: group });
      } else {
        dispatch({
          type: "removeFilterGroupFromActiveFilters",
          payload: group
        });
      }

      window.dataLayer.push({
        event: `switchFilterGroup${e.target.checked ? "On" : "Off"}`,
        filterGroup: filterGroups[group].title
      });
    };

    React.useEffect(() => {
      updateFilterQueryAndSyncWithDatabase(
        activeFilterGroups,
        activeFilters,
        uid
      );
    }, [activeFilterGroups, activeFilters]);

    React.useMemo(() => {
      document.addEventListener("click", event => {
        if (event.target.closest(".calendar__filter")) return;
        setIsExpanded(false);
      });

      document.addEventListener("keydown", event => {
        if (event.key === "Escape") {
          setIsExpanded(false);
        }
      });
    }, []);

    const props = {
      handleOnGroupFilterClick,
      handleOnFilterClick,
      isExpanded,
      setIsExpanded,
      isActive,
      setIsActive,
      filterList,
      filterGroups,
      updateSearchValue,
      searchValue,
      removeActiveFilters,

      ...getFilterList({
        searchValue,
        filterList,
        filterGroups,
        activeFilterGroups,
        activeFilters,
        activeSubtractions
      })
    };

    return window.innerWidth < 991 ? (
      <FilterPopup {...props} />
    ) : (
      <FilterBar {...props} />
    );
  }
);

const getFilterList = ({
  searchValue,
  filterList,
  filterGroups,
  activeFilterGroups,
  activeFilters,
  activeSubtractions
}) => {
  const _filterList = Object.keys(filterList)
    .map(i => {
      const filter = filterList[i];
      let checked =
        activeFilters.indexOf(filter.id) !== -1 &&
        activeSubtractions.indexOf(filter.id) === -1;

      const inGroup = filterGroups.findIndex(group => {
        if (
          group.group === filter.group &&
          activeFilterGroups.indexOf(group.id) !== -1
        ) {
          if (!group.region || filter.region.indexOf(group.region) !== -1) {
            return true;
          }
          return false;
        }

        return false;
      });

      if (inGroup !== -1 && activeSubtractions.indexOf(filter.id) === -1) {
        checked = true;
      }

      if (
        typeof filterGroups[filter.group] !== "undefined" &&
        activeFilterGroups.indexOf(filterGroups[filter.group].id) !== -1 &&
        activeSubtractions.indexOf(filter.id) === -1
      ) {
        checked = true;
      }

      return {
        title: filter.title,
        id: filter.id,
        checked
      };
    })
    .filter(
      filter => searchValue.length === 0 || textMatch(searchValue, filter.title)
    );

  const _filterGroups = filterGroups.map(group => {
    return {
      title: group.title,
      id: group.id,
      checked: activeFilterGroups.indexOf(group.id) !== -1
    };
  });

  return { filterList: _filterList, filterGroups: _filterGroups };
};

/**
 * Function for updating the active filters
 *
 * When a custom user calendar is created the active filters
 * are also synced with the server.
 */
export const updateFilterQueryAndSyncWithDatabase = (groups, filters, uid) => {
  // Create the new query with updated filters
  const updatedQuery = {
    ...queryString.parse(window.location.search.replace("?", "")),
    filters: filters.join("."),
    groups: groups.join(".")
  };

  // Remove filters from the query when it's empty
  if (filters.length === 0) {
    delete updatedQuery.filters;
  }

  if (groups.length === 0 || (groups.length === 1 && groups[0] === "1")) {
    delete updatedQuery.groups;
  }

  // Did not change
  if (
    window.location.search.replace("?", "") ===
    queryString.stringify(updatedQuery)
  ) {
    return;
  }

  // Create the new url and push it into the history API
  const newUrl = `${window.location.origin}${
    window.location.pathname
  }?${queryString.stringify(updatedQuery)}`;
  window.history.pushState({}, "", newUrl);

  // Start syncinc with server when uid is set
  if (uid == null) return;

  // Create the body for the request
  const body = queryString.stringify({
    nonce: wp.nonce,
    action: "update_active_filters",
    created_by: uid,
    active_filter_groups: JSON.stringify(groups),
    active_filters: JSON.stringify(filters)
  });

  // Do nothing with the result (only log errors)
  fetch(wp.ajaxUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: new Headers({
      "Content-Type": "application/x-www-form-urlencoded"
    }),
    body: body
  }).catch(err => {
    console.error(err);
  });
};
